import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { Conversation, ChatMessage, User, UserRole, Payment } from '../../types';
import { useTheme } from '../../theme';
import { AnimatedIcon } from '../../components/icons';
import { paymentService } from '../../services/paymentService';
import { proposalService } from '../../services/proposalService';
import { extractApiError } from '../../services/apiClient';

interface ChatRoomProps {
  conversation: Conversation | null;
  messages: ChatMessage[];
  onSend: (content: string, replyToId?: string) => void;
  onBlockUser?: (userId: string, reason: string) => void;
  onRemoveMessage?: (messageId: string) => void;
  onRefresh?: () => void;
  isLoading?: boolean;
}

function fmtTime(iso: string) {
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
}

export const ChatRoom: React.FC<ChatRoomProps> = ({
  conversation,
  messages,
  onSend,
  onBlockUser,
  onRemoveMessage,
  onRefresh,
  isLoading = false,
}) => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [text, setText] = useState('');
  const [replyTo, setReplyTo] = useState<ChatMessage | null>(null);
  const [blockTarget, setBlockTarget] = useState<User | null>(null);
  const [blockReason, setBlockReason] = useState('');
  const [payments, setPayments] = useState<Payment[]>([]);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [paymentMessage, setPaymentMessage] = useState('');
  const [proposalActionId, setProposalActionId] = useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { isDark } = useTheme();
  
  const isAdmin = user?.role === UserRole.ADMIN;
  const isSurveyor = user?.role === UserRole.SURVEYOR;
  const isBlocked = conversation?.is_blocked;

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadPayments = async () => {
    if (!conversation?.deal_id) return;
    try {
      const data = await paymentService.list({ deal: conversation.deal_id });
      setPayments(data.results ?? []);
    } catch {
      setPayments([]);
    }
  };

  useEffect(() => {
    loadPayments();
  }, [conversation?.deal_id]);

  const handleSend = () => {
    if (!text.trim() || isBlocked) return;
    onSend(text.trim(), replyTo?.id);
    setText('');
    setReplyTo(null);
  };

  const grouped = useMemo(() => {
    const groups: { date: string; msgs: ChatMessage[] }[] = [];
    let current: { date: string; msgs: ChatMessage[] } | null = null;
    for (const msg of (messages ?? [])) {
      const d = fmtDate(msg.created_at);
      if (!current || current.date !== d) {
        current = { date: d, msgs: [] };
        groups.push(current);
      }
      current.msgs.push(msg);
    }
    return groups;
  }, [messages]);

  const otherParticipants = (conversation?.participants ?? []).filter(p => p.id !== user?.id);
  const latestPayment = payments[0];
  const pendingPayments = payments.filter((payment) => payment.status === 'pending_approval');

  const handlePopFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !conversation) return;
    setPaymentLoading(true);
    setPaymentError('');
    setPaymentMessage('');
    try {
      await paymentService.submitPop({
        conversation_id: conversation.id,
        deal_id: conversation.deal_id,
        proof_image: file,
      });
      setPaymentMessage('Payment POP submitted for admin approval.');
      await loadPayments();
    } catch (err) {
      setPaymentError(extractApiError(err));
    } finally {
      setPaymentLoading(false);
      event.target.value = '';
    }
  };

  const approvePayment = async (paymentId: string) => {
    setPaymentLoading(true);
    setPaymentError('');
    try {
      await paymentService.approve(paymentId);
      setPaymentMessage('Payment POP approved.');
      await loadPayments();
      onRefresh?.();
    } catch (err) {
      setPaymentError(extractApiError(err));
    } finally {
      setPaymentLoading(false);
    }
  };

  const rejectPayment = async (paymentId: string) => {
    const reason = window.prompt('Reason for rejecting this POP?', 'Payment proof could not be verified.');
    if (reason === null) return;
    setPaymentLoading(true);
    setPaymentError('');
    try {
      await paymentService.reject(paymentId, reason);
      setPaymentMessage('Payment POP rejected.');
      await loadPayments();
      onRefresh?.();
    } catch (err) {
      setPaymentError(extractApiError(err));
    } finally {
      setPaymentLoading(false);
    }
  };
  
  const chatTitle = conversation
    ? conversation.type === 'REQUEST'
      ? `Project Chat #${conversation.ticket_number}`
      : `Support Chat #${conversation.ticket_number}`
    : 'Chat';

  const chatPartner = otherParticipants.map(p => `${p.first_name} ${p.last_name}`).join(', ') || 'Support Agent';

  const handleProposalAction = async (proposalId: string, action: 'accept' | 'reject') => {
    if (!proposalId || proposalActionId) return;
    if (action === 'reject') {
      const confirmed = window.confirm('Decline this offer? The job will go back on the market and this chat will close.');
      if (!confirmed) return;
    }
    setProposalActionId(proposalId);
    setPaymentError('');
    setPaymentMessage('');
    try {
      if (action === 'accept') {
        const deal = await proposalService.accept(proposalId);
        setPaymentMessage('Offer approved. Surveyor must upload POP for admin approval.');
        onRefresh?.();
        if (deal?.id) navigate(`/client/deal/${deal.id}`);
      } else {
        await proposalService.reject(proposalId);
        setPaymentMessage('Offer declined. This chat is closed and the job is back on the market.');
        onRefresh?.();
      }
    } catch (err) {
      setPaymentError(extractApiError(err));
    } finally {
      setProposalActionId(null);
    }
  };

  return (
    <div style={styles.wrapper} className="sc-animate-scale-in">
      {/* Header */}
      <div style={styles.header}>
        <button style={styles.backBtn} onClick={() => navigate(-1)} className="sc-press">
          ← Back
        </button>
        <div style={{ flex: 1 }}>
          <h2 style={styles.headerTitle}>{chatTitle}</h2>
          <div style={styles.headerSub}>{chatPartner}</div>
        </div>
        {conversation && (
          <div style={styles.ticketTag}>
            Ticket #{conversation.ticket_number}
          </div>
        )}
      </div>

      {/* Blocked banner */}
      {isBlocked && (
        <div style={styles.blockedBanner}>
          <AnimatedIcon name="warning" size={14} color="var(--sc-error-text)" />
          <span style={{ marginLeft: 6 }}>This conversation has been locked by administration.</span>
          {conversation?.blocked_reason && (
            <span style={{ fontStyle: 'italic', marginLeft: 6 }}>({conversation.blocked_reason})</span>
          )}
        </div>
      )}

      {/* Admin moderation controls */}
      {isAdmin && conversation && (
        <div style={styles.adminBar}>
          <span style={styles.adminLabel}>⚙ Platform Oversight Controls</span>
          <div style={styles.adminActions}>
            {otherParticipants.map(p => (
              <button
                key={p.id}
                onClick={() => setBlockTarget(p)}
                style={styles.adminBlockBtn}
                className="sc-press"
              >
                Block {p.first_name}
              </button>
            ))}
            {conversation.is_blocked ? (
              <button
                onClick={() => onBlockUser?.(conversation.id, '__unblock__')}
                style={styles.adminUnblockBtn}
                className="sc-press"
              >Unblock Chat</button>
            ) : (
              <button
                onClick={() => onBlockUser?.(conversation.id, 'Violation of platform rules')}
                style={styles.adminBlockBtn}
                className="sc-press"
              >Block Chat</button>
            )}
          </div>
        </div>
      )}

      {conversation && (isSurveyor || isAdmin) && (
        <div style={styles.paymentPanel}>
          <div style={styles.paymentPanelHeader}>
            <div>
              <div style={styles.paymentTitle}>Payment POP</div>
              <div style={styles.paymentSub}>
                {latestPayment
                  ? `Latest status: ${latestPayment.status.replace('_', ' ')}`
                  : 'Upload proof to clear the current job balance.'}
              </div>
            </div>
            {isSurveyor && !isBlocked && (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={handlePopFile}
                />
                <button
                  type="button"
                  style={styles.uploadPopBtn}
                  disabled={paymentLoading}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {paymentLoading ? 'Uploading...' : 'Upload POP'}
                </button>
              </>
            )}
          </div>
          {paymentMessage && <div style={styles.paymentSuccess}>{paymentMessage}</div>}
          {paymentError && <div style={styles.paymentError}>{paymentError}</div>}
          {isAdmin && pendingPayments.length > 0 && (
            <div style={styles.paymentReviewList}>
              {pendingPayments.map((payment) => (
                <div key={payment.id} style={styles.paymentReviewItem}>
                  {payment.proof_url ? (
                    <a href={payment.proof_url} target="_blank" rel="noreferrer" style={styles.proofThumbLink}>
                      <img src={payment.proof_url} alt="Payment proof" style={styles.proofThumb} />
                    </a>
                  ) : (
                    <div style={styles.proofMissing}>No image</div>
                  )}
                  <div style={{ flex: 1 }}>
                    <div style={styles.paymentAmount}>${Number(payment.amount).toFixed(2)}</div>
                    <div style={styles.paymentSub}>{payment.surveyor_email || 'Surveyor'} submitted POP</div>
                  </div>
                  <button type="button" style={styles.approvePopBtn} disabled={paymentLoading} onClick={() => approvePayment(payment.id)}>
                    Approve
                  </button>
                  <button type="button" style={styles.rejectPopBtn} disabled={paymentLoading} onClick={() => rejectPayment(payment.id)}>
                    Reject
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Messages Feed */}
      <div style={styles.messageList}>
        {isLoading && messages.length === 0 && (
          <div style={styles.loadBox}>
            <div style={styles.spinner} />
          </div>
        )}
        {grouped.map(group => (
          <React.Fragment key={group.date}>
            <div style={styles.dateDivider}>
              <span style={styles.datePill}>{group.date}</span>
            </div>
            {group.msgs.map(msg => {
              const isMe = msg.sender?.id === user?.id;
              const isSys = msg.message_type === 'SYSTEM';
              const isDealProposal = msg.message_type === 'DEAL_PROPOSAL';
              const isMsgBlocked = msg.is_blocked || msg.message_type === 'BLOCKED';

              if (isDealProposal) {
                const canActOnProposal = user?.role === UserRole.CLIENT && !!msg.proposal_id && !isBlocked;
                const isProposalBusy = proposalActionId === msg.proposal_id;
                return (
                  <div key={msg.id} style={styles.proposalMsg}>
                    <div style={styles.proposalTitle}>Surveyor Offer</div>
                    <div style={styles.proposalText}>{msg.content}</div>
                    {user?.role === UserRole.SURVEYOR && (
                      <div style={styles.proposalMuted}>Offer sent. Waiting for client approval.</div>
                    )}
                    {user?.role === UserRole.CLIENT && (
                      <div style={styles.proposalActions}>
                        <button
                          type="button"
                          style={{ ...styles.proposalBtn, ...(canActOnProposal ? {} : styles.proposalBtnDisabled) }}
                          disabled={!canActOnProposal || isProposalBusy}
                          onClick={() => msg.proposal_id && handleProposalAction(msg.proposal_id, 'accept')}
                        >
                          {isProposalBusy ? 'Working...' : 'Approve'}
                        </button>
                        <button
                          type="button"
                          style={{ ...styles.proposalDeclineBtn, ...(canActOnProposal ? {} : styles.proposalBtnDisabled) }}
                          disabled={!canActOnProposal || isProposalBusy}
                          onClick={() => msg.proposal_id && handleProposalAction(msg.proposal_id, 'reject')}
                        >
                          Decline
                        </button>
                      </div>
                    )}
                  </div>
                );
              }

              if (isSys) {
                return (
                  <div key={msg.id} style={styles.systemMsg}>{msg.content}</div>
                );
              }

              if (isMsgBlocked) {
                return (
                  <div key={msg.id} style={styles.blockedMsg}>
                    <AnimatedIcon name="warning" size={12} color="var(--sc-error-text)" />
                    <span style={{ marginLeft: 6 }}>{msg.blocked_reason || 'Message blocked by admin'}</span>
                  </div>
                );
              }

              return (
                <div key={msg.id} style={{ ...styles.bubbleRow, justifyContent: isMe ? 'flex-end' : 'flex-start' }}>
                  <div style={{ ...styles.bubble, ...(isMe ? styles.myBubble : styles.otherBubble) }}>
                    {!isMe && <div style={styles.senderName}>{msg.sender?.first_name} {msg.sender?.last_name}</div>}
                    {msg.reply_to_message && (
                      <div style={{ ...styles.replySnippet, ...(isMe ? styles.myReplySnippet : {}) }}>
                        <div style={styles.replyAuthor}>
                          {msg.reply_to_message.sender?.id === user?.id
                            ? 'You'
                            : `${msg.reply_to_message.sender?.first_name || ''} ${msg.reply_to_message.sender?.last_name || ''}`.trim() || 'Message'}
                        </div>
                        <div style={styles.replyText}>{msg.reply_to_message.content}</div>
                      </div>
                    )}
                    <div style={styles.bubbleText}>{msg.content}</div>
                    <div style={styles.messageMeta}>
                      <span>{fmtTime(msg.created_at)}</span>
                      {isMe && <span>{msg.read_at ? 'Read' : msg.delivered_at ? 'Delivered' : 'Sent'}</span>}
                    </div>
                    <button
                      onClick={() => setReplyTo(msg)}
                      style={styles.replyBtn}
                      title="Reply"
                    >
                      Reply
                    </button>
                    
                    {/* Admin delete message button */}
                    {isAdmin && onRemoveMessage && (
                      <button
                        onClick={() => onRemoveMessage(msg.id)}
                        style={styles.removeBtn}
                        title="Remove message"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </React.Fragment>
        ))}
        <div ref={bottomRef} />
      </div>

      {/* Input controls */}
      {isBlocked ? (
        <div style={styles.closedBar}>This conversation is blocked. Messaging is disabled.</div>
      ) : (
        <div style={styles.inputBar}>
          {replyTo && (
            <div style={styles.replyBar}>
              <div style={styles.replyBarLine} />
              <div style={styles.replyBarBody}>
                <div style={styles.replyBarTitle}>Replying to {replyTo.sender?.id === user?.id ? 'yourself' : replyTo.sender?.first_name || 'message'}</div>
                <div style={styles.replyBarText}>{replyTo.content}</div>
              </div>
              <button style={styles.replyBarClose} onClick={() => setReplyTo(null)} title="Cancel reply">×</button>
            </div>
          )}
          <input
            style={styles.input}
            placeholder="Type a message..."
            value={text}
            onChange={e => setText(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleSend()}
          />
          <button 
            style={{ 
              ...styles.sendBtn, 
              background: text.trim() ? 'var(--sc-brand-gradient)' : 'var(--sc-bg-input)',
              color: text.trim() ? '#FFF' : 'var(--sc-text-tertiary)'
            }} 
            onClick={handleSend} 
            disabled={!text.trim()}
            className="sc-press"
          >
            Send
          </button>
        </div>
      )}

      {/* Admin block dialog */}
      {blockTarget && (
        <div style={styles.modalOverlay} onClick={() => setBlockTarget(null)}>
          <div style={styles.modal} onClick={e => e.stopPropagation()} className="sc-animate-scale-bounce">
            <h3 style={styles.modalTitle}>Block {blockTarget.first_name} {blockTarget.last_name}</h3>
            <p style={styles.modalText}>Provide a reason for blocking this user from this conversation:</p>
            <textarea
              style={styles.modalTextarea}
              placeholder="Reason for blocking..."
              value={blockReason}
              onChange={e => setBlockReason(e.target.value)}
              rows={3}
            />
            <div style={styles.modalActions}>
              <button style={styles.modalCancel} onClick={() => { setBlockTarget(null); setBlockReason(''); }} className="sc-press">Cancel</button>
              <button
                style={styles.modalConfirm}
                onClick={() => {
                  onBlockUser?.(blockTarget.id, blockReason || 'Violation of platform rules');
                  setBlockTarget(null);
                  setBlockReason('');
                }}
                className="sc-press"
              >
                Block User
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  wrapper: { display: 'flex', flexDirection: 'column', height: 'calc(100vh - 120px)', background: 'var(--sc-bg-primary)', borderRadius: 'var(--sc-radius-lg)', overflow: 'hidden', border: '1px solid var(--sc-border)', boxShadow: 'var(--sc-shadow-card)', maxWidth: 800, margin: '0 auto' },

  header: { display: 'flex', alignItems: 'center', gap: 14, padding: '14px 20px', background: 'var(--sc-bg-secondary)', borderBottom: '1px solid var(--sc-border-light)' },
  proposalMsg: { alignSelf: 'center', maxWidth: 520, width: 'min(100%, 520px)', background: 'var(--sc-warning-bg)', border: '1px solid var(--sc-warning)', borderRadius: 'var(--sc-radius-md)', padding: '12px 14px', margin: '8px auto', color: 'var(--sc-warning-text)' },
  proposalTitle: { fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 },
  proposalText: { fontSize: 13, lineHeight: 1.45, marginBottom: 10 },
  proposalActions: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  proposalBtn: { height: 34, borderRadius: 'var(--sc-radius-sm)', border: 'none', background: 'var(--sc-brand-primary)', color: '#FFF', fontSize: 12, fontWeight: 800, padding: '0 12px', cursor: 'pointer' },
  proposalDeclineBtn: { height: 34, borderRadius: 'var(--sc-radius-sm)', border: '1px solid var(--sc-error)', background: 'var(--sc-error-bg)', color: 'var(--sc-error-text)', fontSize: 12, fontWeight: 800, padding: '0 12px', cursor: 'pointer' },
  proposalBtnDisabled: { opacity: 0.45, cursor: 'not-allowed', filter: 'grayscale(0.8)' },
  proposalMuted: { fontSize: 12, fontWeight: 700, color: 'var(--sc-text-secondary)', background: 'var(--sc-bg-input)', borderRadius: 'var(--sc-radius-sm)', padding: '8px 10px' },
  backBtn: { background: 'none', border: 'none', fontSize: 13, color: 'var(--sc-brand-primary)', cursor: 'pointer', padding: 0, fontFamily: 'inherit', fontWeight: 700 },
  headerTitle: { fontSize: 16, fontWeight: 800, color: 'var(--sc-text-primary)', margin: 0, fontFamily: 'var(--sc-font-display)' },
  headerSub: { fontSize: 12, color: 'var(--sc-text-secondary)', marginTop: 2 },
  ticketTag: { fontSize: 11, fontWeight: 700, background: 'var(--sc-success-bg)', color: 'var(--sc-success-text)', borderRadius: 20, padding: '4px 12px', whiteSpace: 'nowrap' },

  blockedBanner: { padding: '10px 20px', background: 'var(--sc-error-bg)', borderBottom: '1px solid var(--sc-error)', fontSize: 13, color: 'var(--sc-error-text)', fontWeight: 600, display: 'flex', alignItems: 'center' },

  adminBar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 20px', background: 'var(--sc-warning-bg)', borderBottom: '1px solid var(--sc-warning)', fontSize: 12 },
  adminLabel: { fontWeight: 700, color: 'var(--sc-warning-text)' },
  adminActions: { display: 'flex', gap: 6 },
  adminBlockBtn: { fontSize: 11, padding: '5px 12px', borderRadius: 'var(--sc-radius-sm)', background: 'var(--sc-error-bg)', color: 'var(--sc-error-text)', border: '1px solid var(--sc-error)', cursor: 'pointer', fontWeight: 700, fontFamily: 'inherit' },
  adminUnblockBtn: { fontSize: 11, padding: '5px 12px', borderRadius: 'var(--sc-radius-sm)', background: 'var(--sc-success-bg)', color: 'var(--sc-success-text)', border: '1px solid var(--sc-success)', cursor: 'pointer', fontWeight: 700, fontFamily: 'inherit' },

  paymentPanel: { padding: '10px 20px', background: 'var(--sc-bg-secondary)', borderBottom: '1px solid var(--sc-border-light)' },
  paymentPanelHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 },
  paymentTitle: { fontSize: 13, fontWeight: 800, color: 'var(--sc-text-primary)' },
  paymentSub: { fontSize: 11, color: 'var(--sc-text-secondary)', marginTop: 2, textTransform: 'capitalize' },
  uploadPopBtn: { height: 34, padding: '0 12px', border: 'none', borderRadius: 8, background: 'var(--sc-brand-primary)', color: '#FFF', fontSize: 12, fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit' },
  paymentSuccess: { marginTop: 8, padding: '7px 10px', borderRadius: 8, border: '1px solid var(--sc-success)', background: 'var(--sc-success-bg)', color: 'var(--sc-success-text)', fontSize: 12, fontWeight: 700 },
  paymentError: { marginTop: 8, padding: '7px 10px', borderRadius: 8, border: '1px solid var(--sc-error)', background: 'var(--sc-error-bg)', color: 'var(--sc-error-text)', fontSize: 12, fontWeight: 700 },
  paymentReviewList: { display: 'flex', flexDirection: 'column', gap: 8, marginTop: 10 },
  paymentReviewItem: { display: 'flex', alignItems: 'center', gap: 10, border: '1px solid var(--sc-border-light)', borderRadius: 8, padding: 8, background: 'var(--sc-bg-input)' },
  proofThumbLink: { width: 54, height: 54, borderRadius: 6, overflow: 'hidden', border: '1px solid var(--sc-border)', flex: '0 0 auto' },
  proofThumb: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
  proofMissing: { width: 54, height: 54, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--sc-bg-hover)', color: 'var(--sc-text-tertiary)', fontSize: 10, border: '1px solid var(--sc-border)' },
  paymentAmount: { fontSize: 13, fontWeight: 800, color: 'var(--sc-text-primary)' },
  approvePopBtn: { height: 30, border: '1px solid var(--sc-success)', borderRadius: 7, padding: '0 10px', color: 'var(--sc-success-text)', background: 'var(--sc-success-bg)', fontSize: 11, fontWeight: 800, cursor: 'pointer' },
  rejectPopBtn: { height: 30, border: '1px solid var(--sc-error)', borderRadius: 7, padding: '0 10px', color: 'var(--sc-error-text)', background: 'var(--sc-error-bg)', fontSize: 11, fontWeight: 800, cursor: 'pointer' },

  messageList: { flex: 1, overflowY: 'auto', padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 8 },
  loadBox: { display: 'flex', justifyContent: 'center', padding: 20 },
  spinner: { width: 24, height: 24, border: '3px solid var(--sc-border)', borderTopColor: 'var(--sc-brand-primary)', borderRadius: '50%', animation: 'sc-spin 0.8s linear infinite' },

  dateDivider: { textAlign: 'center', margin: '16px 0 8px' },
  datePill: { fontSize: 11, fontWeight: 600, color: 'var(--sc-text-secondary)', background: 'var(--sc-bg-hover)', borderRadius: 20, padding: '4px 12px', border: '1px solid var(--sc-border-light)' },

  systemMsg: { textAlign: 'center', fontSize: 11, color: 'var(--sc-text-secondary)', padding: '6px 12px', background: 'var(--sc-bg-input)', borderRadius: 20, margin: '4px auto', maxWidth: '80%', border: '1px solid var(--sc-border-light)' },
  blockedMsg: { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: 'var(--sc-error-bg)', borderRadius: 'var(--sc-radius-md)', fontSize: 12, color: 'var(--sc-error-text)', margin: '4px auto', maxWidth: '80%', border: '1px solid var(--sc-error)' },

  bubbleRow: { display: 'flex', marginBottom: 2 },
  bubble: { maxWidth: '75%', borderRadius: 16, padding: '10px 16px', position: 'relative' },
  myBubble: { background: 'var(--sc-brand-primary-bg)', color: 'var(--sc-brand-primary)', borderBottomRightRadius: 2, fontWeight: 500 },
  otherBubble: { background: 'var(--sc-bg-secondary)', color: 'var(--sc-text-primary)', borderBottomLeftRadius: 2, border: '1px solid var(--sc-border)' },
  senderName: { fontSize: 10, fontWeight: 700, color: 'var(--sc-brand-primary-light)', marginBottom: 4 },
  bubbleText: { fontSize: 13.5, lineHeight: 1.5, wordBreak: 'break-word' },
  replySnippet: { borderLeft: '3px solid var(--sc-brand-primary)', background: 'var(--sc-bg-input)', borderRadius: 6, padding: '6px 8px', marginBottom: 7, maxWidth: '100%' },
  myReplySnippet: { background: 'rgba(255,255,255,0.55)' },
  replyAuthor: { fontSize: 10.5, fontWeight: 800, color: 'var(--sc-brand-primary)', marginBottom: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  replyText: { fontSize: 12, color: 'var(--sc-text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  messageMeta: { fontSize: 10, color: 'var(--sc-text-tertiary)', marginTop: 4, textAlign: 'right', fontWeight: 500, display: 'flex', justifyContent: 'flex-end', gap: 7 },
  replyBtn: { marginTop: 6, padding: 0, border: 'none', background: 'none', color: 'var(--sc-brand-primary)', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' },
  removeBtn: { position: 'absolute', top: 4, right: -24, fontSize: 10, background: 'var(--sc-error-bg)', color: 'var(--sc-error-text)', border: '1px solid var(--sc-error)', borderRadius: '50%', width: 18, height: 18, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },

  closedBar: { padding: '14px 20px', background: 'var(--sc-error-bg)', textAlign: 'center', fontSize: 13, color: 'var(--sc-error-text)', fontWeight: 700, borderTop: '1px solid var(--sc-error)' },
  inputBar: { display: 'flex', gap: 10, padding: '10px 20px 14px', background: 'var(--sc-bg-secondary)', borderTop: '1px solid var(--sc-border-light)', flexWrap: 'wrap' },
  replyBar: { width: '100%', display: 'flex', alignItems: 'center', gap: 9, padding: '8px 10px', border: '1px solid var(--sc-border-light)', borderRadius: 8, background: 'var(--sc-bg-input)' },
  replyBarLine: { width: 3, height: 32, borderRadius: 2, background: 'var(--sc-brand-primary)', flex: '0 0 auto' },
  replyBarBody: { flex: 1, minWidth: 0 },
  replyBarTitle: { fontSize: 12, fontWeight: 800, color: 'var(--sc-text-primary)' },
  replyBarText: { fontSize: 12, color: 'var(--sc-text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginTop: 2 },
  replyBarClose: { width: 26, height: 26, borderRadius: 13, border: 'none', background: 'transparent', color: 'var(--sc-text-tertiary)', fontSize: 18, cursor: 'pointer' },
  input: { flex: 1, height: 'var(--sc-touch-input)', padding: '0 16px', border: '1px solid var(--sc-border)', borderRadius: 24, fontSize: 14, outline: 'none', fontFamily: 'inherit', color: 'var(--sc-text-primary)', background: 'var(--sc-bg-input)' },
  sendBtn: { height: 40, padding: '0 20px', border: 'none', borderRadius: 20, fontWeight: 700, fontSize: 13.5, cursor: 'pointer', fontFamily: 'inherit', transition: 'all var(--sc-transition-fast)' },

  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'var(--sc-bg-overlay)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 },
  modal: { background: 'var(--sc-bg-secondary)', borderRadius: 'var(--sc-radius-lg)', padding: '24px 28px', width: 400, maxWidth: '90vw', boxShadow: 'var(--sc-shadow-xl)', border: '1px solid var(--sc-border)' },
  modalTitle: { fontSize: 16, fontWeight: 800, color: 'var(--sc-text-primary)', margin: '0 0 8px', fontFamily: 'var(--sc-font-display)' },
  modalText: { fontSize: 13, color: 'var(--sc-text-secondary)', margin: '0 0 12px' },
  modalTextarea: { width: '100%', padding: '10px 14px', border: '1px solid var(--sc-border)', borderRadius: 'var(--sc-radius-md)', fontSize: 13, fontFamily: 'inherit', outline: 'none', resize: 'vertical', background: 'var(--sc-bg-input)', color: 'var(--sc-text-primary)' },
  modalActions: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 },
  modalCancel: { padding: '8px 18px', borderRadius: 'var(--sc-radius-md)', border: '1px solid var(--sc-border)', background: 'transparent', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit', color: 'var(--sc-text-secondary)' },
  modalConfirm: { padding: '8px 18px', borderRadius: 'var(--sc-radius-md)', border: 'none', background: 'var(--sc-error)', color: '#FFF', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' },
};
